import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { TechLabel } from "@/components/Bits";
import usePageMeta from "@/hooks/usePageMeta";
import { COMPANY, SERVICES } from "@/config/company";
import { Phone, Mail, AtSign, MapPin, Clock, MessageCircle, ChevronDown, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT =
  "w-full border border-white/10 bg-ink/60 px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-molten";

export default function Contatti() {
  usePageMeta(
    "Contatti — MMC S.r.l. | Carpenteria Metallica",
    "Contatta MMC S.r.l.: telefono, email, PEC, indirizzo, orari e modulo per richiedere un preventivo su misura."
  );

  const [sending, setSending] = useState(false);
  const [ref, setRef] = useState(null);
  const [form, setForm] = useState({
    nome: "",
    azienda: "",
    telefono: "",
    email: "",
    tipo_lavorazione: SERVICES[0].title,
    messaggio: "",
    privacy_consent: false,
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/preventivi`, form);
      setRef(data.id);
      toast.success("Richiesta inviata correttamente!");
      setForm({
        nome: "",
        azienda: "",
        telefono: "",
        email: "",
        tipo_lavorazione: SERVICES[0].title,
        messaggio: "",
        privacy_consent: false,
      });
    } catch (err) {
      toast.error("Errore nell'invio. Riprova o contattaci direttamente.");
    } finally {
      setSending(false);
    }
  };

  const contactRows = [
    { icon: Phone, label: "Telefono", value: COMPANY.phoneDisplay, href: COMPANY.phoneHref, testid: "contact-phone" },
    { icon: Mail, label: "Email", value: COMPANY.emailDisplay, href: COMPANY.emailHref, testid: "contact-email" },
    { icon: AtSign, label: "PEC", value: COMPANY.pecDisplay, href: COMPANY.pecHref, testid: "contact-pec" },
    { icon: MapPin, label: "Sede", value: COMPANY.addressDisplay, href: null, testid: "contact-address" },
  ];

  return (
    <main>
      <PageHeader
        index="06"
        eyebrow="Parliamone"
        title="Contatti"
        sub="Telefono, email, PEC o officina: scegli il canale che preferisci. Per i preventivi usa il modulo, ti rispondiamo in breve tempo."
      />

      <section data-testid="contatti-main" className="px-5 py-16 md:px-10 md:py-24 lg:px-16">
        <div className="mx-auto grid max-w-7xl gap-14 lg:grid-cols-12">
          {/* INFO */}
          <div className="lg:col-span-5">
            <Reveal>
              <TechLabel className="text-molten">Recapiti</TechLabel>
              <ul className="mt-6 space-y-6">
                {contactRows.map((r) => (
                  <li key={r.label} data-testid={r.testid} className="flex items-start gap-4 border-b border-white/10 pb-6">
                    <r.icon className="mt-1 size-5 shrink-0 text-molten" strokeWidth={1.5} />
                    <div>
                      <TechLabel>{r.label}</TechLabel>
                      {r.href ? (
                        <a
                          href={r.href}
                          data-testid={`${r.testid}-link`}
                          className="mt-1.5 block text-lg text-white/85 transition-colors duration-300 hover:text-molten"
                        >
                          {r.value}
                        </a>
                      ) : (
                        <p className="mt-1.5 text-lg text-white/85">{r.value}</p>
                      )}
                    </div>
                  </li>
                ))}
                <li className="flex items-start gap-4">
                  <Clock className="mt-1 size-5 shrink-0 text-molten" strokeWidth={1.5} />
                  <div className="flex-1">
                    <TechLabel>Orari</TechLabel>
                    <ul className="mt-3 space-y-2.5">
                      {COMPANY.hours.map((h) => (
                        <li key={h.d} className="flex items-baseline justify-between gap-6 border-b border-white/5 pb-2 text-sm">
                          <span className="text-white/75">{h.d}</span>
                          <span className="font-tech text-xs tracking-wider text-white/60">{h.h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              </ul>
            </Reveal>

            <Reveal delay={0.1}>
              <a
                href={COMPANY.whatsappHref || "#modulo-preventivo"}
                data-testid="contact-whatsapp-btn"
                className="mt-10 inline-flex items-center gap-3 border border-molten/60 px-6 py-4 font-tech text-[11px] uppercase tracking-[0.2em] text-molten transition-colors duration-300 hover:bg-molten hover:text-ink"
              >
                <MessageCircle className="size-4" /> Scrivici su WhatsApp
              </a>
            </Reveal>
          </div>

          {/* FORM */}
          <Reveal delay={0.15} className="lg:col-span-7">
            <div id="modulo-preventivo" data-testid="contact-form-panel" className="border border-white/10 bg-steel p-6 md:p-10">
              {ref ? (
                <div className="flex flex-col items-start gap-5 py-10">
                  <CheckCircle2 className="size-10 text-molten" strokeWidth={1.5} />
                  <h2 className="font-display text-3xl font-semibold uppercase tracking-tight">Richiesta ricevuta</h2>
                  <p className="max-w-md leading-relaxed text-fog">
                    Grazie! Il nostro ufficio tecnico valuterà la tua richiesta e ti ricontatterà al più presto.
                  </p>
                  <p className="border border-white/10 px-4 py-2.5 font-tech text-[11px] tracking-[0.2em] text-molten" data-testid="contact-ref-code">
                    CODICE RICHIESTA: MMC-{String(ref).slice(0, 6).toUpperCase()}
                  </p>
                  <button
                    data-testid="contact-new-request-btn"
                    onClick={() => setRef(null)}
                    className="mt-2 font-tech text-[11px] uppercase tracking-[0.2em] text-white/60 underline-offset-4 transition-colors duration-300 hover:text-molten hover:underline"
                  >
                    Invia un'altra richiesta
                  </button>
                </div>
              ) : (
                <form data-testid="contact-form" onSubmit={submit} className="space-y-6">
                  <div>
                    <TechLabel className="text-molten">Modulo preventivo</TechLabel>
                    <h2 className="mt-3 font-display text-3xl font-semibold uppercase tracking-tight">
                      Raccontaci la lavorazione
                    </h2>
                  </div>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block font-tech text-[10px] uppercase tracking-[0.25em] text-white/50">Nome e cognome *</span>
                      <input
                        data-testid="contact-name-input"
                        required
                        value={form.nome}
                        onChange={set("nome")}
                        className={INPUT}
                        placeholder="Mario Rossi"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-2 block font-tech text-[10px] uppercase tracking-[0.25em] text-white/50">Azienda</span>
                      <input
                        data-testid="contact-company-input"
                        value={form.azienda}
                        onChange={set("azienda")}
                        className={INPUT}
                        placeholder="Azienda S.r.l."
                      />
                    </label>
                    <label className="block">
                      <span className="mb-2 block font-tech text-[10px] uppercase tracking-[0.25em] text-white/50">Email *</span>
                      <input
                        data-testid="contact-email-input"
                        required
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        className={INPUT}
                        placeholder="nome@azienda.it"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-2 block font-tech text-[10px] uppercase tracking-[0.25em] text-white/50">Telefono</span>
                      <input
                        data-testid="contact-phone-input"
                        type="tel"
                        value={form.telefono}
                        onChange={set("telefono")}
                        className={INPUT}
                        placeholder="+39 ..."
                      />
                    </label>
                  </div>
                  <label className="relative block">
                    <span className="mb-2 block font-tech text-[10px] uppercase tracking-[0.25em] text-white/50">Tipo di lavorazione</span>
                    <select data-testid="contact-type-select" value={form.tipo_lavorazione} onChange={set("tipo_lavorazione")} className={`${INPUT} appearance-none pr-10`}>
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.title} className="bg-steel">
                          {s.title}
                        </option>
                      ))}
                      <option value="Altro" className="bg-steel">
                        Altro
                      </option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute bottom-4 right-4 size-4 text-molten" />
                  </label>
                  <label className="block">
                    <span className="mb-2 block font-tech text-[10px] uppercase tracking-[0.25em] text-white/50">Messaggio *</span>
                    <textarea
                      data-testid="contact-message-input"
                      required
                      rows={5}
                      value={form.messaggio}
                      onChange={set("messaggio")}
                      className={`${INPUT} resize-none`}
                      placeholder="Descrivi la lavorazione, le quantità e i tempi previsti..."
                    />
                  </label>
                  <label className="flex items-start gap-3 text-sm text-white/70">
                    <input
                      data-testid="contact-privacy-checkbox"
                      required
                      type="checkbox"
                      checked={form.privacy_consent}
                      onChange={(e) => setForm((f) => ({ ...f, privacy_consent: e.target.checked }))}
                      className="mt-1 size-4 shrink-0 appearance-none border border-white/25 bg-ink/60 checked:border-molten checked:bg-molten"
                    />
                    <span>
                      Ho letto e accetto l'<a href="/informazioni-legali" className="text-molten underline-offset-4 hover:underline">informativa sulla privacy</a> e
                      acconsento al trattamento dei miei dati per essere ricontattato. *
                    </span>
                  </label>
                  <button
                    data-testid="contact-submit-btn"
                    type="submit"
                    disabled={sending}
                    className="w-full bg-molten px-7 py-4 font-tech text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-molten-hot disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {sending ? "Invio in corso..." : "Invia la richiesta"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MAPPA */}
      <section data-testid="contatti-mappa" className="border-t border-white/10">
        <TechLabel className="block px-5 pt-6 md:px-10 lg:px-16">Mappa — zona sede (aggiornare con l'indirizzo reale)</TechLabel>
        <iframe
          title="Mappa MMC S.r.l."
          src={COMPANY.mapEmbed}
          loading="lazy"
          className="dark-map mt-5 h-[360px] w-full border-0 grayscale"
        />
      </section>
    </main>
  );
}
